import { useEffect, useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import client, { apiError, API } from "../../lib/api";
import Seo from "../../components/Seo";
import { LogOut, Plus, Trash2, Upload, Ban } from "lucide-react";

function Section({ title, children, testId }) {
  return (
    <section className="rounded-2xl border border-white/10 bg-[#181824] p-6" data-testid={testId}>
      <h2 className="font-display text-2xl text-white">{title}</h2>
      <div className="mt-4">{children}</div>
    </section>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [signups, setSignups] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [screeners, setScreeners] = useState([]);
  const [codes, setCodes] = useState([]);
  const [clips, setClips] = useState([]);
  const [newCode, setNewCode] = useState("");
  const [clipForm, setClipForm] = useState({ title: "", caption: "" });
  const [notice, setNotice] = useState("");
  const [tab, setTab] = useState("overview");

  const load = () => {
    client.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    client.get("/admin/signups").then((r) => setSignups(r.data)).catch(() => {});
    client.get("/admin/contacts").then((r) => setContacts(r.data)).catch(() => {});
    client.get("/admin/screener-requests").then((r) => setScreeners(r.data)).catch(() => {});
    client.get("/admin/codes").then((r) => setCodes(r.data)).catch(() => {});
    client.get("/admin/clips").then((r) => setClips(r.data)).catch(() => {});
  };
  useEffect(load, []);

  if (user === null) return null;

  const addCode = async (e) => {
    e.preventDefault();
    try {
      await client.post("/admin/codes", { code: newCode });
      setNewCode("");
      setNotice("Code added.");
      load();
    } catch (err) { setNotice(apiError(err)); }
  };

  const revokeCode = async (id) => {
    await client.post(`/admin/codes/${id}/revoke`);
    load();
  };

  const addClip = async (e) => {
    e.preventDefault();
    await client.post("/admin/clips", { ...clipForm, order: clips.length });
    setClipForm({ title: "", caption: "" });
    setNotice("Clip added. Upload its video below.");
    load();
  };

  const uploadClipVideo = async (id, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    await client.post(`/admin/clips/${id}/video`, fd);
    setNotice("Clip video uploaded.");
    load();
  };

  const uploadEpisode = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await client.post("/admin/episode/upload", fd, { timeout: 600000 });
      setNotice("Episode uploaded and ready for code-protected playback.");
      load();
    } catch (err) { setNotice(apiError(err)); }
  };

  const tabs = ["overview", "signups", "contacts", "screeners", "codes", "clips", "video"];

  return (
    <main className="pt-16 min-h-screen" data-testid="admin-dashboard">
      <Seo title="Admin" noindex />
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="font-display text-4xl text-white">Backstage</h1>
          <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2 text-sm text-gray-300 hover:text-white">
            <LogOut size={15} /> Sign out
          </button>
        </div>
        {notice && <p role="status" className="mt-4 rounded-lg border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-4 py-2 text-sm text-[#F59E0B]" data-testid="admin-notice">{notice}</p>}

        <div className="mt-6 flex flex-wrap gap-2" role="tablist">
          {tabs.map((t) => (
            <button key={t} onClick={() => setTab(t)} role="tab" aria-selected={tab === t} data-testid={`admin-tab-${t}`}
              className={`rounded-full px-4 py-2 text-sm font-medium capitalize ${tab === t ? "bg-[#E11D48] text-white" : "border border-white/15 text-gray-300"}`}>
              {t}
            </button>
          ))}
        </div>

        <div className="mt-6 space-y-6">
          {tab === "overview" && stats && (
            <Section title="Overview" testId="admin-overview">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-center">
                {[["List signups", stats.signups], ["Contact messages", stats.contacts], ["Screener requests", stats.screener_requests], ["Active codes", stats.codes], ["BTS clips", stats.clips], ["Episode uploaded", stats.episode_uploaded ? "Yes" : "Not yet"]].map(([k, v]) => (
                  <div key={k} className="rounded-xl bg-black/40 p-5">
                    <p className="font-display text-3xl text-[#F59E0B]">{v}</p>
                    <p className="mt-1 text-xs uppercase tracking-widest text-gray-400">{k}</p>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {tab === "signups" && (
            <Section title="Join the List Signups" testId="admin-signups">
              <a href={`${API}/admin/signups/export`} data-testid="admin-export-csv"
                className="inline-block rounded-full bg-[#F59E0B] px-5 py-2 text-sm font-bold text-black">Export CSV</a>
              <ul className="mt-4 divide-y divide-white/5 text-sm" data-testid="admin-signups-list">
                {signups.map((s) => (
                  <li key={s.id} className="py-2 flex flex-wrap gap-x-4 text-gray-300">
                    <span className="text-white">{s.email}</span>
                    {s.first_name && <span>{s.first_name}</span>}
                    {s.city && <span>{s.city}</span>}
                    {s.interests?.length > 0 && <span className="text-[#F59E0B]">{s.interests.join(", ")}</span>}
                  </li>
                ))}
                {signups.length === 0 && <li className="py-4 text-gray-500">No signups yet.</li>}
              </ul>
            </Section>
          )}

          {tab === "contacts" && (
            <Section title="Contact Messages" testId="admin-contacts">
              <ul className="divide-y divide-white/5 text-sm" data-testid="admin-contacts-list">
                {contacts.map((c) => (
                  <li key={c.id} className="py-3 text-gray-300">
                    <span className="text-[#F59E0B] uppercase text-xs tracking-widest">{c.topic}</span>
                    <p className="text-white">{c.name} — {c.email}</p>
                    <p className="mt-1">{c.message}</p>
                  </li>
                ))}
                {contacts.length === 0 && <li className="py-4 text-gray-500">No messages yet.</li>}
              </ul>
            </Section>
          )}

          {tab === "screeners" && (
            <Section title="Screener Requests" testId="admin-screeners">
              <ul className="divide-y divide-white/5 text-sm" data-testid="admin-screeners-list">
                {screeners.map((c) => (
                  <li key={c.id} className="py-3 text-gray-300">
                    <p className="text-white">{c.name} — {c.email}</p>
                    {c.message && <p className="mt-1">{c.message}</p>}
                  </li>
                ))}
                {screeners.length === 0 && <li className="py-4 text-gray-500">No requests yet.</li>}
              </ul>
            </Section>
          )}

          {tab === "codes" && (
            <Section title="Viewing Codes" testId="admin-codes">
              <form onSubmit={addCode} className="flex flex-wrap gap-3" data-testid="admin-add-code-form">
                <input required minLength={6} value={newCode} onChange={(e) => setNewCode(e.target.value)}
                  placeholder="New code (min 6 chars)" data-testid="admin-new-code"
                  className="flex-1 rounded-xl border border-white/15 bg-black/40 px-4 py-2.5 text-white placeholder-gray-500 outline-none focus:border-[#F59E0B]" />
                <button type="submit" data-testid="admin-add-code" className="inline-flex items-center gap-2 rounded-full bg-[#E11D48] px-5 py-2.5 text-sm font-semibold text-white">
                  <Plus size={15} /> Add code
                </button>
              </form>
              <ul className="mt-4 divide-y divide-white/5 text-sm" data-testid="admin-codes-list">
                {codes.map((c) => (
                  <li key={c.id} className="py-2 flex items-center justify-between text-gray-300">
                    <span>{c.label} — {c.active ? <span className="text-emerald-400">active</span> : <span className="text-red-400">revoked</span>}</span>
                    {c.active && (
                      <button onClick={() => revokeCode(c.id)} data-testid={`admin-revoke-${c.id}`} className="inline-flex items-center gap-1 text-red-400 text-xs">
                        <Ban size={13} /> Revoke
                      </button>
                    )}
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-xs text-gray-500">Code values are stored hashed — share codes with buyers directly, and revoke any time.</p>
            </Section>
          )}

          {tab === "clips" && (
            <Section title="Behind the Scenes Clips" testId="admin-clips">
              <form onSubmit={addClip} className="grid gap-3" data-testid="admin-add-clip-form">
                <input required value={clipForm.title} onChange={(e) => setClipForm({ ...clipForm, title: e.target.value })}
                  placeholder="Clip title" data-testid="admin-clip-title"
                  className="rounded-xl border border-white/15 bg-black/40 px-4 py-2.5 text-white placeholder-gray-500 outline-none focus:border-[#F59E0B]" />
                <input value={clipForm.caption} onChange={(e) => setClipForm({ ...clipForm, caption: e.target.value })}
                  placeholder="Short caption" data-testid="admin-clip-caption"
                  className="rounded-xl border border-white/15 bg-black/40 px-4 py-2.5 text-white placeholder-gray-500 outline-none focus:border-[#F59E0B]" />
                <button type="submit" data-testid="admin-add-clip" className="w-fit rounded-full bg-[#E11D48] px-5 py-2.5 text-sm font-semibold text-white">Add clip</button>
              </form>
              <ul className="mt-4 divide-y divide-white/5 text-sm" data-testid="admin-clips-list">
                {clips.map((c) => (
                  <li key={c.id} className="py-3 flex flex-wrap items-center justify-between gap-3 text-gray-300">
                    <span className="text-white">{c.title} {c.published ? "" : <span className="text-xs text-gray-500">(unpublished)</span>} {c.storage_path ? "— video attached" : ""}</span>
                    <span className="flex gap-3">
                      <label className="inline-flex cursor-pointer items-center gap-1 text-[#F59E0B] text-xs">
                        <Upload size={13} /> Upload video
                        <input type="file" accept="video/*" className="hidden" data-testid={`admin-clip-upload-${c.id}`} onChange={(e) => uploadClipVideo(c.id, e.target.files[0])} />
                      </label>
                      <button onClick={async () => { await client.delete(`/admin/clips/${c.id}`); load(); }} data-testid={`admin-clip-delete-${c.id}`} className="inline-flex items-center gap-1 text-red-400 text-xs">
                        <Trash2 size={13} /> Delete
                      </button>
                    </span>
                  </li>
                ))}
                {clips.length === 0 && <li className="py-4 text-gray-500">No clips yet. The public gallery stays unpublished until a clip has a video.</li>}
              </ul>
            </Section>
          )}

          {tab === "video" && (
            <Section title="Protected Episode" testId="admin-video">
              <p className="text-sm text-gray-400">Upload the web-streaming version of Episode 4 (H.264 .mp4 recommended, up to ~100MB through this panel; for the full-quality MOV, ask us to load it directly).</p>
              <label className="mt-4 inline-flex cursor-pointer items-center gap-2 rounded-full bg-[#F59E0B] px-6 py-3 text-sm font-bold text-black" data-testid="admin-episode-upload-label">
                <Upload size={16} /> Upload episode file
                <input type="file" accept="video/mp4,video/quicktime,.mp4,.mov" className="hidden" data-testid="admin-episode-upload" onChange={(e) => uploadEpisode(e.target.files[0])} />
              </label>
              <p className="mt-3 text-sm text-gray-300">Status: {stats?.episode_uploaded ? <span className="text-emerald-400">Uploaded and streaming to valid code holders</span> : <span className="text-[#F59E0B]">Awaiting file</span>}</p>
            </Section>
          )}
        </div>
      </div>
    </main>
  );
}
